import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight, RefreshCw, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { SpotCapacityOverview, SpotLifetimeOverview } from "@/components/spot-capacity-overview";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { usePageTitle } from "@/hooks/use-page-title";
import { usePreemptions } from "@/hooks/use-preemptions";
import { useSpotStatistics } from "@/hooks/use-spot-statistics";
import {
  formatPreemptionDuration,
  formatPreemptionRate,
  formatPreemptionTimestamp,
  PREEMPTION_WINDOWS,
  type PreemptionFacet,
  preemptionFacetLabel,
  readPreemptionWindow,
} from "@/lib/preemptions";
import type { MachineRunEntry, PreemptionGroup, PreemptionsResponse } from "@/lib/types";
import { cn } from "@/lib/utils";

const PAGE_SIZE = 20;
const FACETS = [
  { key: "provider", label: "Provider", dimension: "providers" },
  { key: "gpu_type", label: "GPU", dimension: "gpu_types" },
  { key: "region", label: "Region", dimension: "regions" },
  { key: "fabric", label: "Fabric", dimension: "fabrics" },
] as const;
const GROUPS = [
  { key: "fabric", label: "Fabric", data: "by_fabric" },
  { key: "gpu_type", label: "GPU type", data: "by_gpu_type" },
  { key: "region", label: "Region", data: "by_region" },
] as const;
const AXIS_STYLE = {
  tick: { fontSize: 11, fill: "var(--muted-foreground)" },
  tickLine: false,
  axisLine: false,
};
const OUTCOME_LABELS: Record<string, string> = {
  preempted: "Preempted",
  stopped: "Stopped",
  missing: "Missing",
  deleted: "Deleted",
  restarted: "Restarted",
  running: "Running at window end",
};

type ChartMetric = "preemptions" | "rate";
type GroupSort = "preemptions" | "machine_hours" | "rate" | "median_preempted_lifetime_hours";

interface ChartPoint {
  start?: string;
  hour?: number;
  preemptions: number;
  matched_preemptions: number;
  machine_hours: number;
  rate: number | null;
}

function number(value: number): string {
  return value.toLocaleString("en-US", { maximumFractionDigits: 1 });
}

function ChartTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: ReadonlyArray<{ payload?: ChartPoint }>;
}) {
  const point = payload?.[0]?.payload;
  if (!active || !point) return null;
  return (
    <div className="rounded-lg border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-md">
      <div className="mb-2 font-medium">
        {point.start
          ? `${formatPreemptionTimestamp(point.start)} UTC`
          : `${String(point.hour).padStart(2, "0")}:00–${String((point.hour ?? 0) + 1).padStart(2, "0")}:00 UTC`}
      </div>
      <div>{number(point.preemptions)} confirmed preemptions</div>
      <div className="text-muted-foreground">
        {number(point.machine_hours)} observed machine-hours
      </div>
      <div className="mt-1">
        {point.rate == null
          ? "Rate unavailable: no observed machine-hours"
          : `${formatPreemptionRate(point.rate)} per 1,000 machine-hours (${number(point.matched_preemptions)} matched events)`}
      </div>
    </div>
  );
}

function AnalyticsChart({
  points,
  metric,
  hourly = false,
  shortWindow = false,
}: {
  points: ChartPoint[];
  metric: ChartMetric;
  hourly?: boolean;
  shortWindow?: boolean;
}) {
  return (
    <ResponsiveContainer width="100%" height={240}>
      <BarChart
        data={points}
        margin={{ top: 8, right: 8, bottom: 4, left: -20 }}
        accessibilityLayer
      >
        <CartesianGrid vertical={false} stroke="var(--border)" strokeDasharray="3 3" />
        <XAxis
          dataKey={hourly ? "hour" : "start"}
          {...AXIS_STYLE}
          minTickGap={24}
          tickFormatter={(value: string | number) =>
            hourly
              ? String(value).padStart(2, "0")
              : new Date(value).toLocaleString("en-US", {
                  timeZone: "UTC",
                  month: "short",
                  day: "numeric",
                  ...(shortWindow ? { hour: "2-digit", hourCycle: "h23" as const } : {}),
                })
          }
        />
        <YAxis {...AXIS_STYLE} allowDecimals={metric === "rate"} />
        <Tooltip content={<ChartTooltip />} cursor={{ fill: "var(--muted)", opacity: 0.6 }} />
        <Bar
          dataKey={metric}
          name={metric === "rate" ? "Per 1,000 machine-hours" : "Preemptions"}
          fill={metric === "rate" ? "var(--primary)" : "var(--chart-1)"}
          radius={[3, 3, 0, 0]}
          maxBarSize={34}
          isAnimationActive={false}
        />
      </BarChart>
    </ResponsiveContainer>
  );
}

function FacetSelect({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
}) {
  const available = [...new Set([...options, ...(value ? [value] : [])])];
  return (
    <div className="min-w-0 space-y-1">
      <label className="text-[11px] font-medium text-muted-foreground">{label}</label>
      <Select
        value={value || "all"}
        onValueChange={(next) => next && onChange(next === "all" ? "" : next)}
      >
        <SelectTrigger
          className="w-full min-w-32 text-xs"
          aria-label={`Filter by ${label.toLowerCase()}`}
        >
          <SelectValue>
            {value
              ? preemptionFacetLabel(value)
              : `All ${label === "GPU" ? "GPUs" : `${label.toLowerCase()}s`}`}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">
            All {label === "GPU" ? "GPUs" : `${label.toLowerCase()}s`}
          </SelectItem>
          {available.map((option) => (
            <SelectItem key={option} value={option}>
              {preemptionFacetLabel(option)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function GroupComparison({
  data,
  onFilter,
}: {
  data: PreemptionsResponse;
  onFilter: (filters: Record<string, string>) => void;
}) {
  const [group, setGroup] = useState<(typeof GROUPS)[number]["key"]>("fabric");
  const [sort, setSort] = useState<GroupSort>("machine_hours");
  const [ascending, setAscending] = useState(false);
  const selected = GROUPS.find((option) => option.key === group) ?? GROUPS[0];
  const rows = useMemo(
    () =>
      [...data[selected.data]].sort((first, second) => {
        const firstValue = first[sort];
        const secondValue = second[sort];
        if (firstValue == null)
          return secondValue == null ? first.key.localeCompare(second.key) : 1;
        if (secondValue == null) return -1;
        return (
          (ascending ? firstValue - secondValue : secondValue - firstValue) ||
          first.key.localeCompare(second.key)
        );
      }),
    [data, selected.data, sort, ascending],
  );

  function sortHeader(key: GroupSort, title: string) {
    return (
      <button
        type="button"
        className="ml-auto inline-flex items-center gap-1 whitespace-nowrap"
        onClick={() => {
          setAscending(sort === key ? !ascending : false);
          setSort(key);
        }}
      >
        {title}
        {sort === key ? (
          ascending ? (
            <ArrowUp className="size-3" />
          ) : (
            <ArrowDown className="size-3" />
          )
        ) : null}
      </button>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-wrap items-center justify-between gap-3 sm:flex-row">
        <div>
          <CardTitle>Compare {selected.label.toLowerCase()}s</CardTitle>
          <CardDescription className="mt-1 text-xs">
            Select a group to filter this view. More observed hours make rates more reliable.
          </CardDescription>
        </div>
        <fieldset className="flex gap-1" aria-label="Compare preemptions by">
          {GROUPS.map((option) => (
            <Button
              key={option.key}
              size="sm"
              variant={group === option.key ? "secondary" : "ghost"}
              aria-pressed={group === option.key}
              onClick={() => setGroup(option.key)}
            >
              {option.label}
            </Button>
          ))}
        </fieldset>
      </CardHeader>
      <CardContent className="px-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="pl-4">{selected.label}</TableHead>
              <TableHead
                className="text-right"
                aria-sort={
                  sort === "preemptions" ? (ascending ? "ascending" : "descending") : "none"
                }
              >
                {sortHeader("preemptions", "Preemptions")}
              </TableHead>
              <TableHead
                className="text-right"
                aria-sort={
                  sort === "machine_hours" ? (ascending ? "ascending" : "descending") : "none"
                }
              >
                {sortHeader("machine_hours", "Machine-hours")}
              </TableHead>
              <TableHead
                className="text-right"
                aria-sort={sort === "rate" ? (ascending ? "ascending" : "descending") : "none"}
              >
                {sortHeader("rate", "Per 1,000 machine-hours")}
              </TableHead>
              <TableHead
                className="pr-4 text-right"
                aria-sort={
                  sort === "median_preempted_lifetime_hours"
                    ? ascending
                      ? "ascending"
                      : "descending"
                    : "none"
                }
              >
                {sortHeader("median_preempted_lifetime_hours", "Median before preemption")}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row: PreemptionGroup) => (
              <TableRow key={row.key}>
                <TableCell className="pl-4">
                  <button
                    type="button"
                    className="font-medium text-primary hover:underline"
                    onClick={() =>
                      onFilter(
                        group === "fabric"
                          ? {
                              fabric: row.fabric ?? "unknown",
                              provider: row.provider ?? "unknown",
                              region: row.region ?? "unknown",
                            }
                          : { [group]: row.key },
                      )
                    }
                  >
                    {preemptionFacetLabel(group === "fabric" ? (row.fabric ?? "unknown") : row.key)}
                  </button>
                  <span className="ml-2 text-[11px] text-muted-foreground">{row.runs} runs</span>
                  {group === "fabric" && (
                    <div className="mt-1 text-[11px] text-muted-foreground">
                      {row.provider} · {preemptionFacetLabel(row.region ?? "unknown")}
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-right font-mono tabular-nums">
                  {number(row.preemptions)}
                </TableCell>
                <TableCell className="text-right font-mono tabular-nums">
                  {number(row.machine_hours)}
                </TableCell>
                <TableCell className="text-right font-mono tabular-nums">
                  {formatPreemptionRate(row.rate)}
                </TableCell>
                <TableCell className="pr-4 text-right font-mono tabular-nums">
                  {formatPreemptionDuration(row.median_preempted_lifetime_hours)}
                  {row.known_lifetime_count > 0 && (
                    <span className="ml-1 text-[11px] text-muted-foreground">
                      ({number(row.known_lifetime_count)} runs)
                    </span>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="h-20 text-center text-muted-foreground">
                  No groups match this window and these filters.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <p className="px-4 pt-3 text-[11px] text-muted-foreground">
          Rates use confirmed preemptions matched to an observed run; — means no observed hours.
          Medians include only preempted runs with known starts.
        </p>
      </CardContent>
    </Card>
  );
}

function RunRuntime({ run }: { run: MachineRunEntry }) {
  const running = run.end_reason === "running";
  return (
    <div className="whitespace-nowrap text-right">
      <div className="font-mono tabular-nums">
        {run.started_at
          ? `${running ? "≥ " : ""}${formatPreemptionDuration(run.lifetime_hours)}`
          : `≥ ${formatPreemptionDuration(run.observed_hours)}`}
      </div>
      <div className="mt-1 text-[10px] text-muted-foreground">
        {!run.started_at
          ? "Start unknown · observed in window"
          : running
            ? "Running at window end"
            : "Known start to end"}
      </div>
    </div>
  );
}

function MachineRuns({
  data,
  page,
  search,
  onPage,
  onSearch,
}: {
  data: PreemptionsResponse;
  page: number;
  search: string;
  onPage: (page: number) => void;
  onSearch: (search: string) => void;
}) {
  const first = data.runs_total > 0 ? page * PAGE_SIZE + 1 : 0;
  const last = Math.min((page + 1) * PAGE_SIZE, data.runs_total);
  return (
    <Card>
      <CardHeader className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
        <div>
          <CardTitle>Machine runs</CardTitle>
          <CardDescription className="mt-1 text-xs">
            A new run begins after each restart. History remains after a machine is deleted.
          </CardDescription>
        </div>
        <div className="relative w-full sm:w-60">
          <Search className="pointer-events-none absolute left-2.5 top-2 size-3.5 text-muted-foreground" />
          <Input
            className="pl-8 text-xs"
            aria-label="Search machine runs"
            maxLength={200}
            placeholder="Search machine name or ID…"
            value={search}
            onChange={(event) => onSearch(event.target.value)}
          />
        </div>
      </CardHeader>
      <CardContent className="px-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="pl-4">Machine</TableHead>
              <TableHead>Location / fabric</TableHead>
              <TableHead>Observed from (UTC)</TableHead>
              <TableHead>End (UTC)</TableHead>
              <TableHead className="text-right">Runtime</TableHead>
              <TableHead className="pr-4">Outcome</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.runs.map((run: MachineRunEntry) => (
              <TableRow key={run.id}>
                <TableCell className="pl-4">
                  {run.machine_id ? (
                    <Link
                      to={`/machines/${run.machine_id}`}
                      className="font-medium text-primary hover:underline"
                    >
                      {run.name}
                    </Link>
                  ) : (
                    <span className="font-medium">{run.name}</span>
                  )}
                  <div className="mt-1 text-[11px] text-muted-foreground">
                    {run.provider} · {run.gpu_count}× {run.gpu_type.toUpperCase()}
                  </div>
                  <div
                    className="mt-0.5 max-w-60 truncate font-mono text-[10px] text-muted-foreground"
                    title={run.provider_id}
                  >
                    {run.provider_id}
                  </div>
                </TableCell>
                <TableCell className="text-xs">
                  <div>{preemptionFacetLabel(run.region)}</div>
                  <div className="mt-1 text-muted-foreground">
                    {preemptionFacetLabel(run.fabric)}
                  </div>
                </TableCell>
                <TableCell className="whitespace-nowrap text-xs">
                  <div>{formatPreemptionTimestamp(run.observed_started_at)}</div>
                  <div className="mt-1 text-[10px] text-muted-foreground">
                    {run.started_at
                      ? `Started ${formatPreemptionTimestamp(run.started_at)}`
                      : "Running when first observed"}
                  </div>
                </TableCell>
                <TableCell className="whitespace-nowrap text-xs">
                  {run.ended_at ? formatPreemptionTimestamp(run.ended_at) : "—"}
                </TableCell>
                <TableCell>
                  <RunRuntime run={run} />
                </TableCell>
                <TableCell className="pr-4">
                  <Badge
                    variant="outline"
                    className={cn(
                      "whitespace-nowrap text-[10px]",
                      run.end_reason === "preempted" &&
                        "border-destructive/20 bg-destructive/10 text-destructive",
                    )}
                  >
                    {OUTCOME_LABELS[run.end_reason] ?? run.end_reason}
                  </Badge>
                </TableCell>
              </TableRow>
            ))}
            {data.runs.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="h-24 text-center text-muted-foreground">
                  {search
                    ? "No machine names or IDs match your search."
                    : "No observed machine runs match this window and these filters."}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 pt-3 text-xs text-muted-foreground">
          <span>
            {first}–{last} of {number(data.runs_total)} runs{search && " matching search"}
          </span>
          <div className="flex gap-1">
            <Button
              variant="outline"
              size="sm"
              aria-label="Previous page of machine runs"
              disabled={page === 0}
              onClick={() => onPage(page - 1)}
            >
              <ChevronLeft className="size-3.5" />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              aria-label="Next page of machine runs"
              disabled={last >= data.runs_total}
              onClick={() => onPage(page + 1)}
            >
              Next
              <ChevronRight className="size-3.5" />
            </Button>
          </div>
        </div>
        <p className="px-4 pt-3 text-[11px] leading-relaxed text-muted-foreground">
          ≥ means the run may be longer: it was still running at the window end or its start is
          unknown. Machine-hours count only observations within the selected window. Stops and
          missing machines count as preemptions only after provider confirmation.
        </p>
      </CardContent>
    </Card>
  );
}

export function SpotStatisticsPage() {
  usePageTitle("Spot statistics");
  const [parameters, setParameters] = useSearchParams();
  const [now, setNow] = useState(Date.now);
  const [metric, setMetric] = useState<ChartMetric>("rate");
  const window = readPreemptionWindow(parameters, now);
  const requestedPage = Number(parameters.get("page"));
  const page = Number.isSafeInteger(requestedPage) && requestedPage > 0 ? requestedPage : 0;
  const search = parameters.get("run_search") ?? "";
  const facets = Object.fromEntries(FACETS.map(({ key }) => [key, parameters.get(key) ?? ""]));
  const globalFilters = { provider: facets.provider ?? "", gpu_type: facets.gpu_type ?? "" };
  const overview = usePreemptions(
    {
      start: window.start,
      end: window.end,
      ...globalFilters,
      run_limit: String(PAGE_SIZE),
      run_offset: "0",
      run_search: "",
    },
    !window.error,
  );
  const scoped = Boolean(facets.region || facets.fabric || page || search);
  const diagnostics = usePreemptions(
    {
      start: window.start,
      end: window.end,
      ...facets,
      run_limit: String(PAGE_SIZE),
      run_offset: String(page * PAGE_SIZE),
      run_search: search,
    },
    !window.error && scoped,
  );
  const { data, isLoading, isFetching, isPlaceholderData, error, refetch } = scoped
    ? diagnostics
    : overview;
  const capacity = useSpotStatistics(
    { start: window.start, end: window.end, ...globalFilters },
    !window.error,
  );

  function updateParameters(values: Record<string, string>, replace = false) {
    setParameters(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.delete("page");
        for (const [key, value] of Object.entries(values)) {
          if (value) next.set(key, value);
          else next.delete(key);
        }
        return next;
      },
      { replace },
    );
  }

  function setFacet(key: PreemptionFacet, value: string) {
    updateParameters({ [key]: value });
  }

  return (
    <div className="space-y-5 p-4 sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold tracking-tight">Spot statistics</h1>
          <p className="mt-1 text-xs text-muted-foreground">
            Capacity, pool delivery, and how long spot machines run.
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          disabled={isFetching || overview.isFetching || capacity.isFetching || !!window.error}
          onClick={() => {
            const nextNow = Date.now();
            if (
              window.window === "custom" ||
              Math.floor(nextNow / 60_000) === Math.floor(now / 60_000)
            ) {
              void refetch();
              if (scoped) void overview.refetch();
              void capacity.refetch();
            } else {
              setNow(nextNow);
            }
          }}
        >
          <RefreshCw className={cn("size-3.5", isFetching && "animate-spin")} />
          Refresh
        </Button>
      </div>

      <div className="space-y-4 rounded-xl border bg-card p-4">
        <div className="flex flex-wrap items-center gap-3">
          <fieldset className="flex flex-wrap gap-1" aria-label="Spot statistics time window">
            {PREEMPTION_WINDOWS.map((option) => (
              <Button
                size="sm"
                key={option}
                variant={window.window === option ? "default" : "ghost"}
                aria-pressed={window.window === option}
                onClick={() => {
                  setNow(Date.now());
                  updateParameters({
                    window: option,
                    start: option === "custom" ? window.start : "",
                    end: option === "custom" ? window.end : "",
                  });
                }}
              >
                {option === "custom" ? "Custom" : option}
              </Button>
            ))}
          </fieldset>
          {window.window !== "custom" && (
            <span className="text-[11px] text-muted-foreground">
              {formatPreemptionTimestamp(window.start)} – {formatPreemptionTimestamp(window.end)}{" "}
              (UTC)
            </span>
          )}
        </div>
        {window.window === "custom" && (
          <div className="flex flex-wrap items-end gap-3">
            {(["start", "end"] as const).map((key) => (
              <div key={key} className="space-y-1">
                <label
                  htmlFor={`preemptions-${key}`}
                  className="text-[11px] font-medium text-muted-foreground"
                >
                  {key === "start" ? "From" : "To"} (UTC)
                </label>
                <Input
                  id={`preemptions-${key}`}
                  type="datetime-local"
                  className="w-auto text-xs"
                  value={window[key].slice(0, 16)}
                  onChange={(event) =>
                    updateParameters({
                      [key]: event.target.value ? `${event.target.value}:00Z` : "",
                    })
                  }
                />
              </div>
            ))}
          </div>
        )}
        <div className="grid grid-cols-2 items-end gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]">
          {FACETS.filter(({ key }) => key === "provider" || key === "gpu_type").map(
            ({ key, label, dimension }) => (
              <FacetSelect
                key={key}
                label={label}
                value={facets[key] ?? ""}
                options={[
                  ...(overview.data?.dimensions[dimension] ?? []),
                  ...(capacity.data?.pools.map((pool) =>
                    key === "provider" ? pool.provider : pool.gpu_type,
                  ) ?? []),
                ]}
                onChange={(value) => setFacet(key, value)}
              />
            ),
          )}
          <Button
            variant="ghost"
            size="sm"
            className="justify-self-start text-xs text-muted-foreground"
            disabled={!Object.values(facets).some(Boolean)}
            onClick={() => updateParameters({ provider: "", gpu_type: "", region: "", fabric: "" })}
          >
            Clear filters
          </Button>
        </div>
      </div>

      {!window.error &&
        (capacity.error ? (
          <div role="alert" className="rounded-lg border p-4 text-sm">
            Spot capacity could not be loaded.{" "}
            <Button size="sm" variant="outline" onClick={() => void capacity.refetch()}>
              Try again
            </Button>
          </div>
        ) : capacity.data ? (
          <div
            className={cn(capacity.isPlaceholderData && "opacity-60")}
            aria-busy={capacity.isFetching}
          >
            <SpotCapacityOverview data={capacity.data} reliability={overview.data} />
          </div>
        ) : (
          <div role="status" aria-label="Loading spot capacity">
            <Skeleton className="h-32" />
            <Skeleton className="mt-4 h-72" />
          </div>
        ))}
      {!window.error && overview.data && <SpotLifetimeOverview data={overview.data} />}

      {window.error ? (
        <div
          role="alert"
          className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive"
        >
          {window.error}
        </div>
      ) : error ? (
        <div
          role="alert"
          className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm"
        >
          <p>Preemption history could not be loaded.</p>
          <p className="mt-1 text-xs text-muted-foreground">{error.message}</p>
          <Button className="mt-3" size="sm" variant="outline" onClick={() => void refetch()}>
            Try again
          </Button>
        </div>
      ) : isLoading || !data ? (
        <div role="status" aria-label="Loading preemption history" className="space-y-5">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {[0, 1, 2, 3].map((key) => (
              <Skeleton key={key} className="h-28" />
            ))}
          </div>
          <Skeleton className="h-80" />
          <Skeleton className="h-64" />
        </div>
      ) : (
        <div className={cn("space-y-5", isPlaceholderData && "opacity-60")} aria-busy={isFetching}>
          {isPlaceholderData && (
            <p role="status" className="text-xs font-medium text-primary">
              Updating results for the selected window and filters…
            </p>
          )}
          <section
            className="space-y-3 rounded-xl border bg-card p-4"
            aria-label="Reliability filters"
          >
            <div>
              <h2 className="text-sm font-semibold">Reliability by location and time</h2>
              <p className="mt-1 text-xs text-muted-foreground">
                All recorded spot machines. Location filters apply here and to machine runs; pool
                delivery above covers every location.
              </p>
            </div>
            <div className="grid grid-cols-2 items-end gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]">
              {FACETS.filter(({ key }) => key === "region" || key === "fabric").map(
                ({ key, label, dimension }) => (
                  <FacetSelect
                    key={key}
                    label={label}
                    value={facets[key] ?? ""}
                    options={data.dimensions[dimension]}
                    onChange={(value) => setFacet(key, value)}
                  />
                ),
              )}
              <Button
                size="sm"
                variant="ghost"
                disabled={!facets.region && !facets.fabric}
                onClick={() => updateParameters({ region: "", fabric: "" })}
              >
                All locations
              </Button>
            </div>
          </section>
          {data.summary.runs === 0 && data.summary.preemptions === 0 && (
            <div className="rounded-lg border border-dashed p-6 text-center">
              <p className="text-sm font-medium">No preemptions or observed runs in this window</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Try a wider time window or clear the filters. New spot history appears as Fleet
                observes machines.
              </p>
            </div>
          )}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-medium">Interruption patterns</h3>
            <fieldset className="flex gap-1" aria-label="Chart metric">
              <Button
                size="sm"
                variant={metric === "preemptions" ? "secondary" : "ghost"}
                aria-pressed={metric === "preemptions"}
                onClick={() => setMetric("preemptions")}
              >
                Event count
              </Button>
              <Button
                size="sm"
                variant={metric === "rate" ? "secondary" : "ghost"}
                aria-pressed={metric === "rate"}
                onClick={() => setMetric("rate")}
              >
                Per 1,000 machine-hours
              </Button>
            </fieldset>
          </div>
          <div className="grid gap-4 xl:grid-cols-[1.5fr_1fr]">
            <Card>
              <CardHeader>
                <CardTitle>Over time</CardTitle>
                <CardDescription className="text-xs">
                  {metric === "rate"
                    ? "Matched preemptions per 1,000 observed machine-hours"
                    : "Confirmed preemptions"}{" "}
                  · UTC
                </CardDescription>
              </CardHeader>
              <CardContent>
                <AnalyticsChart
                  points={data.timeline}
                  metric={metric}
                  shortWindow={Date.parse(window.end) - Date.parse(window.start) <= 3 * 86_400_000}
                />
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>By hour of day (UTC)</CardTitle>
                <CardDescription className="text-xs">
                  {metric === "rate"
                    ? "Adjusts for how many machines ran at each hour"
                    : "All selected days combined; fleet size affects counts"}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <AnalyticsChart points={data.by_hour} metric={metric} hourly />
              </CardContent>
            </Card>
          </div>
          <GroupComparison data={data} onFilter={updateParameters} />
          <MachineRuns
            data={data}
            page={page}
            search={search}
            onPage={(next) => updateParameters({ page: String(next) })}
            onSearch={(value) => updateParameters({ run_search: value }, true)}
          />
          <details className="rounded-lg border px-4 py-3 text-[11px] text-muted-foreground">
            <summary className="cursor-pointer font-medium">About these measurements</summary>
            <p className="mt-2 leading-relaxed">
              Runtime history{" "}
              {data.coverage_started_at
                ? `began ${formatPreemptionTimestamp(data.coverage_started_at)} (UTC)`
                : "is collecting"}
              . Rates use confirmed interruptions matched to observed running time. Lifetime medians
              include only interrupted runs with known starts; ongoing runs are unfinished. Missing
              inventory and manual stops do not imply an interruption.
            </p>
          </details>
        </div>
      )}
    </div>
  );
}
